import { X } from '@phosphor-icons/react';
import { Dialog as D } from 'radix-ui';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { cx } from './cx';

/**
 * Dugme sa ikonicom „zatvori“ (modal, dokument, meni, kartice): fiksnih 36×36 px i `flex-none`, pa ga dugačak naslov
 * ili opis ne sabija; ikonica ≥ 3:1 (muted), cilj ≥ 24×24 px i vidljiv fokus (WCAG 2.1 AA 1.4.11, 2.4.7, 2.5.8).
 */
export const CLOSE_BUTTON =
  'grid h-9 w-9 flex-none place-items-center self-start rounded-md border-0 bg-transparent text-muted transition-colors hover:bg-bg2 hover:text-text';

export interface ModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  width?: number;
  /** Ako je zadato, sadržaj je <form> i footer dugmad mogu biti type=submit. */
  onSubmit?: (e: React.FormEvent<HTMLFormElement>) => void;
  testId?: string;
}

/** Modal iz dizajna (Novi projekat): 620px, radius 14, overlay navy .55. */
export function Modal({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  width = 620,
  onSubmit,
  testId,
}: ModalProps) {
  const { t } = useTranslation();
  const body = (
    <>
      <div className="flex items-start justify-between gap-4 border-b border-line px-6 py-5">
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <D.Title className="m-0 font-heading text-[19px] font-semibold">{title}</D.Title>
          {description ? (
            <D.Description className="m-0 text-[13px] text-muted">{description}</D.Description>
          ) : (
            <D.Description className="sr-only">{title}</D.Description>
          )}
        </div>
        <D.Close aria-label={t('common:actions.close')} className={CLOSE_BUTTON}>
          <X size={18} aria-hidden />
        </D.Close>
      </div>
      <div className="px-6 py-[22px]">{children}</div>
      {footer ? (
        <div className="flex justify-end gap-2 border-t border-line bg-bg2 px-6 py-4">{footer}</div>
      ) : null}
    </>
  );
  return (
    <D.Root open={open} onOpenChange={onOpenChange}>
      <D.Portal>
        <D.Overlay className="fixed inset-0 z-[100] bg-[rgba(10,27,51,.55)]" />
        <div className="pointer-events-none fixed inset-0 z-[101] grid place-items-center p-6">
          <D.Content
            aria-describedby={description ? undefined : undefined}
            data-testid={testId}
            className="pointer-events-auto flex max-h-[calc(100vh-48px)] w-full flex-col overflow-auto rounded-2xl border border-line bg-surface shadow-card"
            style={{ maxWidth: width }}
          >
            {onSubmit ? (
              <form className="flex flex-col" onSubmit={onSubmit} noValidate>
                {body}
              </form>
            ) : (
              <div className="flex flex-col">{body}</div>
            )}
          </D.Content>
        </div>
      </D.Portal>
    </D.Root>
  );
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  onConfirm,
  danger,
  loading,
  testId,
  children,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  title: string;
  description?: string;
  confirmLabel: string;
  onConfirm: () => void;
  danger?: boolean;
  loading?: boolean;
  testId?: string;
  children?: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      width={460}
      testId={testId}
      footer={
        <>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="min-h-10 rounded-md border border-line2 bg-surface px-4 text-sm font-semibold text-text"
          >
            {t('common:actions.cancel')}
          </button>
          <button
            type="button"
            disabled={loading}
            onClick={onConfirm}
            data-testid={testId ? `${testId}-submit` : undefined}
            className={cx(
              'min-h-10 rounded-md border-0 px-[18px] text-sm font-semibold disabled:opacity-60',
              danger ? 'bg-danger text-white' : 'bg-btn text-btn-fg hover:bg-btn-h',
            )}
          >
            {confirmLabel}
          </button>
        </>
      }
    >
      {description ? <p className="m-0 text-sm leading-[1.5] text-text2">{description}</p> : null}
      {children}
    </Modal>
  );
}

export { D as DialogPrimitive };
